import { safeQuerySelectorAll, safeAddEventListener } from './utils';

interface VideoOptions {
  preventRightClick?: boolean;
  hideControls?: boolean;
  disableDevTools?: boolean;
  targetSelector?: string;
  observeDynamicContent?: boolean;
}

class VideoShield {
  private options: VideoOptions;
  private eventListeners: { element: HTMLElement; type: string; handler: EventListener }[] = [];
  private styleElement: HTMLStyleElement | null = null;
  private mutationObserver: MutationObserver | null = null;

  constructor(options: VideoOptions = {}) {
    this.options = {
      preventRightClick: true,
      hideControls: false,
      disableDevTools: true,
      targetSelector: 'video',
      observeDynamicContent: true, // Default to true
      ...options
    };
    
    this.initialize();
  }

  private initialize(): void {
    try {
      if (typeof document === 'undefined') return; // Skip for SSR
      
      const selector = this.options.targetSelector || 'video';
      this.protectVideoElements(selector);
      
      // Prevent video downloads through dev tools
      if (this.options.disableDevTools) {
        this.interceptMediaElements();
      }
      
      // Observe DOM changes to protect new videos
      if (this.options.observeDynamicContent) {
        this.setupMutationObserver();
      }
    } catch (error) {
      console.error('web-shield: Error initializing VideoShield', error);
    }
  }

  private protectVideoElements(selector: string): void {
    try {
      safeQuerySelectorAll(selector).forEach(el => {
        if (!(el instanceof HTMLVideoElement)) return;
        
        if (this.options.hideControls) {
          el.controls = false;
        }
        
        if (this.options.preventRightClick) {
          const handler = (e: Event) => {
            e.preventDefault();
            return false;
          };
          
          safeAddEventListener(el, 'contextmenu', handler);
          this.eventListeners.push({
            element: el,
            type: 'contextmenu',
            handler
          });
        }
      });
    } catch (error) {
      console.error('web-shield: Error protecting video elements', error);
    }
  }

  private interceptMediaElements(): void {
    try {
      const selector = this.options.targetSelector || 'video';
      // Create a more specific selector that only targets videos matching our selector
      const specificSelector = selector.split(',')
        .map(s => s.trim() + '::-webkit-media-controls-enclosure, ' + 
                  s.trim() + '::-webkit-media-controls-panel, ' +
                  s.trim() + '::-webkit-media-controls-overlay-play-button')
        .join(', ');

      // Add a style to hide video source (with cross-browser support)
      this.styleElement = document.createElement('style');
      this.styleElement.innerHTML = `
        /* Chrome, Safari, and Opera */
        ${specificSelector},
        
        /* Firefox */
        ${selector.split(',').map(s => s.trim() + '::-moz-media-controls').join(', ')},
        
        /* Edge and IE */
        ${selector.split(',').map(s => s.trim() + '::-ms-media-controls, ' + 
                                 s.trim() + '::-ms-media-controls-play-pause').join(', ')},
        
        /* Generic */
        ${selector.split(',').map(s => s.trim() + '::media-controls').join(', ')} {
          display: none !important;
          opacity: 0 !important;
          visibility: hidden !important;
        }
      `;
      document.head.appendChild(this.styleElement);
    } catch (error) {
      console.error('web-shield: Error setting up video style protection', error);
    }
  }

  private setupMutationObserver(): void {
    try {
      if (!window.MutationObserver) return; // Skip if not supported
      
      const selector = this.options.targetSelector || 'video';
      
      this.mutationObserver = new MutationObserver((mutations) => {
        mutations.forEach(mutation => {
          if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
            // Check if any added nodes match our selector
            mutation.addedNodes.forEach(node => {
              if (node.nodeType === Node.ELEMENT_NODE) {
                // Check if the node itself matches
                if ((node as Element).matches && (node as Element).matches(selector)) {
                  this.protectVideoElements(selector);
                }
                
                // Check children that match
                if (node.hasChildNodes()) {
                  this.protectVideoElements(selector);
                }
              }
            });
          }
        });
      });

      // Start observing
      this.mutationObserver.observe(document.body, { 
        childList: true,
        subtree: true
      });
    } catch (error) {
      console.error('web-shield: Error setting up mutation observer for videos', error);
    }
  }

  disable(): void {
    try {
      this.eventListeners.forEach(({ element, type, handler }) => {
        try {
          element.removeEventListener(type, handler);
        } catch (error) {
          console.warn(`web-shield: Error removing ${type} event listener`, error);
        }
      });
      this.eventListeners = [];
      
      if (this.styleElement) {
        this.styleElement.remove();
      }
      
      if (this.mutationObserver) {
        this.mutationObserver.disconnect();
        this.mutationObserver = null;
      }
      
      // Restore video controls
      safeQuerySelectorAll('video').forEach(video => {
        if (video instanceof HTMLVideoElement) {
          video.controls = true;
        }
      });
    } catch (error) {
      console.error('web-shield: Error disabling VideoShield', error);
    }
  }
}

export default VideoShield;